// src/context/UserContext.jsx

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api/Api';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {

  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);

  // ── Fetch orders from API ──
  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get('orders');
      if (data.status) {
        setOrders(data.data || []);
      }
    } catch (err) {
      console.error('fetchOrders error:', err);
    }
  }, []);

  // ── Fetch user profile ──
  const fetchUser = useCallback(async () => {
    try {
      const { data } = await api.get('profile');
      if (data.status) {
        setUser(data);
      }
    } catch (err) {
      console.error('fetchUser error:', err);
      if (err.response?.status === 401) {
        logout();
      }
    }
  }, []);

  // ── Fetch addresses from API ──
  const fetchAddresses = useCallback(async () => {
    try {
      const res = await api.get('address/list');
      if (res.data.status) {
        setAddresses(res.data.data || []);
      }
    } catch (err) {
      console.error('fetchAddresses error:', err);
      toast.error('Failed to load addresses');
    }
  }, []);

  // ── On app load ──
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetchUser();
    fetchAddresses();
    fetchOrders();
  }, [fetchUser, fetchAddresses, fetchOrders]);

  // ── Auth actions ──

  const login = async (userData) => {
    setUser(userData);
    // Fetch fresh user data from API to ensure all fields are loaded
    await fetchUser();
    fetchAddresses();
    fetchOrders();
    toast.success(`Welcome back, ${userData.name || userData.USER?.name || 'User'}!`);
  };

  const logout = async () => {
    try {
      await api.delete('logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setAddresses([]);
      ['user', 'addresses', 'orders', 'token'].forEach(key =>
        localStorage.removeItem(key)
      );
      toast.info('Logged out successfully');
      window.location.replace('/login');
    }
  };

  const register = (userData) => {
    setUser(userData);
    toast.success('Registration successful! Welcome to Bharti Jewellers!');
  };

  const updateProfile = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
    toast.success('Profile updated successfully');
  };

  // ── Address actions (API) ──

  const addAddress = async (formData) => {
    try {
      const res = await api.post('address/create', formData);
      if (res.data.status) {
        await fetchAddresses();
        toast.success('Address added successfully');
        return res.data;
      }
    } catch (err) {
      console.error('addAddress error:', err);
      toast.error('Failed to add address');
    }
  };

  const updateAddress = async (addressId, formData) => {
    try {
      const res = await api.post('address/update', {
        address_id: addressId,
        ...formData,
      });
      if (res.data.status) {
        await fetchAddresses();
        toast.success('Address updated successfully');
        return res.data;
      }
    } catch (err) {
      console.error('updateAddress error:', err);
      toast.error('Failed to update address');
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      const res = await api.post('address/delete', { address_id: addressId });
      if (res.data.status) {
        setAddresses(prev => prev.filter(a => a.id !== addressId));
        toast.success('Address deleted');
        return true;
      }
    } catch (err) {
      console.error('deleteAddress error:', err);
      toast.error('Delete failed');
    }
  };
  const setPrimary = async (addressId) => {
  try {
    const res = await api.post('address/set-primary', { address_id: addressId });
    if (res.data.status) {
      return true;
    }
  } catch (err) {
    console.error('setPrimary error:', err);
    toast.error('Failed to update primary address');
  }
};

  // ── Order actions ──

  const addOrder = (order) => {
    const newOrder = {
      ...order,
      id: Date.now().toString(),
      orderDate: new Date().toISOString(),
      status: 'Processing',
    };
    setOrders(prev => [newOrder, ...prev]);
    return newOrder;
  };

  const value = {
    user,
    addresses,
    orders,
    login,
    logout,
    register,
    updateProfile,
    addAddress,
    updateAddress,
    deleteAddress,
    addOrder,
    fetchUser,
    fetchAddresses,
    fetchOrders,
    isAuthenticated: !!user,
    setPrimary
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;